package com.eladfinish.bible.contest.screens.mikud.ui.dialogs

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.screens.mikud.mvi.MikudAction
import com.eladfinish.bible.contest.screens.mikud.mvi.MikudState
import com.eladfinish.bible.contest.ui.components.dialogs.BasicDialogWithTitle
import com.eladfinish.bible.contest.ui.components.dialogs.DialogSelectableList
import com.eladfinish.bible.contest.ui.modifiers.alternatingRowBackground
import dev.icerock.moko.resources.compose.localized

@Composable
internal fun LoadMikudDialog(
    state: MikudState.DialogState.LoadMikudMenu,
    onAction: (MikudAction) -> Unit,
) {
    BasicDialogWithTitle(
        title = state.title.localized(),
        onDismiss = { onAction(MikudAction.HideDialog) },
        onCancel = { onAction(MikudAction.HideDialog) },
    ) {
        val totalPresets = state.presets.size
        val totalSaved = state.savedItems.size
        val totalItems = totalPresets + totalSaved
        val itemsIndices = (0 until totalItems).toList()

        DialogSelectableList(
            list = itemsIndices,
            item = { index, _ ->
                val isPreset = index < totalPresets
                val itemText = if (isPreset) {
                    state.presets[index].text
                } else {
                    state.savedItems[index - totalPresets]
                }

                MikudMenuRow(
                    index = index,
                    text = itemText,
                    isPreset = isPreset,
                    onDeleteClicked = {
                        onAction(MikudAction.RequestDeleteSavedMikud(name = itemText))
                    },
                    onRenameClicked = {
                        onAction(MikudAction.RenameSavedMikud(oldName = itemText))
                    }
                )
            },
            onClick = { index, _ ->
                onAction(MikudAction.LoadMikudByIndex(index))
            }
        )
    }
}

@Composable
internal fun MikudMenuRow(
    index: Int,
    text: String,
    isPreset: Boolean,
    onDeleteClicked: () -> Unit = {},
    onRenameClicked: () -> Unit = {},
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .heightIn(min = 56.dp) // Uniform height for all rows
            .padding(horizontal = 16.dp)
            .alternatingRowBackground(index),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = "${index + 1}. $text",
            modifier = Modifier.weight(1f),
            style = MaterialTheme.typography.bodyLarge
        )

        if (!isPreset) {
            TrailingActions(
                onDeleteClicked = onDeleteClicked,
                onRenameClicked = onRenameClicked,
            )
        }
    }
}

@Composable
private fun TrailingActions(
    onDeleteClicked: () -> Unit,
    onRenameClicked: () -> Unit,
) {
    val actionsWidth = 40.dp * 2 // Two icons
    Row(
        modifier = Modifier.width(actionsWidth),
        horizontalArrangement = Arrangement.End,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        RowIcon(
            icon = Icons.Default.Delete,
            contentDescription = "Delete",
            tint = MaterialTheme.colorScheme.error,
            onClick = onDeleteClicked,
        )

        RowIcon(
            icon = Icons.Default.Edit,
            contentDescription = "Rename",
            tint = MaterialTheme.colorScheme.onSurface,
            onClick = onRenameClicked,
        )
    }
}

@Composable
private fun RowIcon(
    icon: ImageVector,
    contentDescription: String,
    tint: Color,
    onClick: () -> Unit,
) {
    Box(
        modifier = Modifier
            .size(40.dp) // Fixed size => consistent row height behavior
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center
    ) {
        Icon(
            imageVector = icon,
            contentDescription = contentDescription,
            tint = tint,
        )
    }
}
